/*!
VueLayers
Vue 2 components to work with OpenLayers

@package vuelayers
@author Vladimir Vershinin <ghettovoice@gmail.com>
@version 0.7.5
@license MIT
@copyright (c) 2017, Vladimir Vershinin <ghettovoice@gmail.com>
*/
'use strict';

function _interopDefault (ex) { return (ex && (typeof ex === 'object') && 'default' in ex) ? ex['default'] : ex; }

var _Object$defineProperties = _interopDefault(require('babel-runtime/core-js/object/define-properties'));
var _Object$create = _interopDefault(require('babel-runtime/core-js/object/create'));
var _merge = _interopDefault(require('lodash/fp/merge'));
var _isEqual = _interopDefault(require('lodash/fp/isEqual'));
var __ol = _interopDefault(require('openlayers'));
var uuid = _interopDefault(require('uuid/v4'));
var Observable = require('rxjs/Observable');
var merge = require('rxjs/observable/merge');
var distinctUntilChanged = require('rxjs/operator/distinctUntilChanged');
var throttleTime = require('rxjs/operator/throttleTime');
var map = require('rxjs/operator/map');
var mergeAll = require('rxjs/operator/mergeAll');
var core = require('../core');

/**
 * @module feature/feature
 */
var Feature = __ol.Feature;
var mergeNArg = _merge.convert({ fixed: false });

/**
 * @vueProps
 */
var props = /** @lends module:feature/feature# */{
  /**
   * Feature identifier.
   * @default UUID
   * @vueSync
   */
  id: {
    type: [String, Number],
    default: function _default() {
      return uuid();
    }
  },
  /**
   * All feature properties.
   * @default {}
   * @vueSync
   */
  properties: {
    type: Object,
    default: function _default() {
      return _Object$create(null);
    }
  }

  /**
   * @vueComputed
   */
};var computed = /** @lends module:feature/feature# */{
  /**
   * **GeoJSON** encoded geometry.
   * @type {GeoJSONFeature|undefined}
   */
  geometry: function geometry() {
    if (this.rev && this.$geometry && this.$view) {
      return core.geoJsonHelper.writeGeometry(this.$geometry, this.$view.getProjection());
    }
  }
};

/**
 * @vueMethods
 */
var methods = /** @lends module:feature/feature# */{
  /**
   * Create feature without inner style applying, feature level style
   * will be applied in the layer level style function.
   * @return {ol.Feature}
   * @protected
   */
  createOlObject: function createOlObject() {
    /**
     * @type {ol.Feature}
     * @private
     */
    var feature = new Feature(this.properties);
    feature.setId(this.id);
    feature.setGeometry(this.$geometry);

    return feature;
  },

  /**
   * @return {{
   *     getGeometry: function(): (ol.geom.Geometry|undefined),
   *     setGeometry: function((ol.geom.Geometry|undefined)): void
   *   }|ol.Feature|undefined}
   * @protected
   */
  getGeometryTarget: function getGeometryTarget() {
    return this.$feature;
  },

  /**
   * @return {Object}
   * @protected
   */
  getServices: function getServices() {
    var vm = this;

    return core.mergeDescriptors(core.olCmp.methods.getServices.call(this), core.geometryContainer.methods.getServices.call(this), core.stylesContainer.methods.getServices.call(this), {
      get feature() {
        return vm.$feature;
      }
    });
  },

  /**
   * @return {ol.Feature|undefined}
   * @protected
   */
  getStyleTarget: function getStyleTarget() {
    return this.$feature;
  },

  /**
   * Checks if feature lies at `pixel`.
   * @param {number} pixel
   * @return {boolean}
   */
  isAtPixel: function isAtPixel(pixel) {
    var _this = this;

    core.assert.hasMap(this);

    return this.$map.forEachFeatureAtPixel(pixel, function (f) {
      return f === _this.$feature;
    }, { layerFilter: function layerFilter(l) {
        return l === _this.$layer;
      } });
  },

  /**
   * @return {void}
   * @protected
   */
  mount: function mount() {
    this.$featuresContainer && this.$featuresContainer.addFeature(this);
    this.subscribeAll();
  },

  /**
   * @return {void}
   * @protected
   */
  unmount: function unmount() {
    this.unsubscribeAll();
    this.$featuresContainer && this.$featuresContainer.removeFeature(this);
  },

  /**
   * @return {void}
   * @protected
   */
  subscribeAll: function subscribeAll() {
    subscribeToFeatureChanges.call(this);
  }
};

var watch = {
  /**
   * @param {string|number} value
   */
  id: function id(value) {
    if (this.$feature && value !== this.$feature.getId()) {
      this.$feature.setId(value);
    }
  },

  /**
   * @param {Object} value
   */
  properties: function properties(value) {
    value = core.plainProps(value);
    if (this.$feature && !_isEqual(value, core.plainProps(this.$feature.getProperties()))) {
      this.$feature.setProperties(core.plainProps(value));
    }
  }
};

/**
 * A vector object for geographic features with a geometry and other attribute properties,
 * similar to the features in vector file formats like **GeoJSON**.
 *
 * @title vl-feature
 * @alias module:feature/feature
 * @vueProto
 *
 * @vueSlot default [scoped] Default **scoped** slot with current feature state: `id`, `properties`, GeoJSON `geometry`.
 */
var Feature$1 = { render: function render() {
    var _vm = this;var _h = _vm.$createElement;var _c = _vm._self._c || _h;return _c('i', { class: [_vm.$options.name], staticStyle: { "display": "none !important" }, attrs: { "id": [_vm.$options.name, _vm.id].join('-') } }, [_vm._t("default", null, { id: _vm.id, properties: _vm.properties, geometry: _vm.geometry })], 2);
  }, staticRenderFns: [],
  name: 'vl-feature',
  mixins: [core.olCmp, core.useMapCmp, core.geometryContainer, core.stylesContainer],
  props: props,
  computed: computed,
  methods: methods,
  watch: watch,
  created: function created() {
    var _this2 = this;

    _Object$defineProperties(this, /** @lends module:feature/feature# */{
      /**
       * Reference to `ol.Feature` instance.
       * @type {ol.Feature|undefined}
       */
      $feature: {
        enumerable: true,
        get: function get() {
          return _this2.$olObject;
        }
      },
      /**
       * Reference to parent `ol.Layer` instance.
       * @type {ol.layer.Layer|undefined}
       */
      $layer: {
        enumerable: true,
        get: function get() {
          return _this2.$services && _this2.$services.layer;
        }
      },
      /**
       * Reference to `ol.Map` instance.
       * @type {ol.Map|undefined}
       */
      $map: {
        enumerable: true,
        get: function get() {
          return _this2.$services && _this2.$services.map;
        }
      },
      /**
       * Reference to `ol.View` instance.
       * @type {ol.View|undefined}
       */
      $view: {
        enumerable: true,
        get: function get() {
          return _this2.$services && _this2.$services.view;
        }
      },
      /**
       * Reference to `featuresContainer`.
       * @type {Object|undefined}
       */
      $featuresContainer: {
        enumerable: true,
        get: function get() {
          return _this2.$services && _this2.$services.featuresContainer;
        }
      }
    });
  }
};

/**
 * @return {void}
 * @private
 */
function subscribeToFeatureChanges() {
  var _this3 = this,
      _context;

  core.assert.hasFeature(this);

  var getPropValue = function getPropValue(prop) {
    return _this3.$feature.get(prop);
  };
  var ft = 100;
  // all plain properties
  var propChanges = (_context = (_context = core.observableFromOlEvent(this.$feature, 'propertychange', function (_ref) {
    var key = _ref.key;
    return { prop: key, value: getPropValue(key) };
  }), throttleTime.throttleTime).call(_context, ft), distinctUntilChanged.distinctUntilChanged).call(_context, _isEqual);
  // id, style and other generic changes
  var changes = (_context = (_context = (_context = (_context = core.observableFromOlEvent(this.$feature, 'change'), map.map).call(_context, function () {
    return Observable.Observable.create(function (obs) {
      if (_this3.$feature.getId() !== _this3.id) {
        obs.next({ prop: 'id', value: _this3.$feature.getId() });
      }
      // todo style?
    });
  }), mergeAll.mergeAll).call(_context), throttleTime.throttleTime).call(_context, ft), distinctUntilChanged.distinctUntilChanged).call(_context, _isEqual);
  // all changes
  var allChanges = merge.merge.call(Observable.Observable, propChanges, changes);

  this.subscribeTo(allChanges, function (_ref2) {
    var prop = _ref2.prop,
        value = _ref2.value;

    ++_this3.rev;

    if (prop === 'id') {
      _this3.$emit('update:' + prop, value);
    } else if (prop !== _this3.$feature.getGeometryName()) {
      _this3.$emit('update:properties', mergeNArg({}, _this3.properties, { prop: value }));
    }
  });
}

/**
 * @module feature
 */
/**
 * @alias module:feature
 *
 * @example Install
 * import Vue from 'vue'
 * // import module and styles
 * import { Feature } from 'vuelayers'
 * // or
 * import Feature from 'vuelayers/lib/feature'
 * // import VueLayers styles
 * import 'vuelayers/lib/style.css'
 * // register components
 * Vue.use(Feature)
 */
var index = {
  /**
   * A vector object for geographic features with a geometry and other attribute properties,
   * similar to the features in vector file formats like **GeoJSON**.
   * @alias module:feature/feature
   */
  Feature: Feature$1,
  install: function install(Vue) {
    Vue.component(Feature$1.name, Feature$1);
  }
};

module.exports = index;

//# sourceMappingURL=index.js.map



//////////////////
// WEBPACK FOOTER
// ./~/vuelayers/lib/feature/index.js
// module id = 1179
// module chunks = 1